<template>
  <div class="exam-list-container">
    <van-nav-bar
      title="课程列表"
      right-text="退出登录"
      @click-right="loginOut"
      fixed
      placeholder
      :z-index="999"
    />
    <div class="header">
      <div class="username">{{ name }}</div>
      <div class="user-info" @click="goToUserInfo">个人信息 ></div>
    </div>
    <div class="content">
      <van-list
        v-if="examList.length"
        v-model:loading="loading"
        :finished="finished"
        finished-text="没有更多了"
        @load="onLoad"
      >
        <van-cell
          v-for="(exam, index) in examList"
          :key="index"
          class="exam-card"
        >
          <div class="exam-title">
            <div class="exam-title-text">{{ exam.title }}</div>
            <div
              v-if="exam.state == 'REGISTERED'"
              class="exam-title-status"
              :style="{ color: getStatusColor(exam) }"
            >
              {{ getStatusText(exam) }}
            </div>
          </div>
          <div v-if="formatCourseTimeRange(exam)" class="course-time-range">
            课程时间：{{ formatCourseTimeRange(exam) }}
          </div>

          <div class="registration-area" v-if="exam.state == 'NOT_REGISTERED'">
            <div class="status-text unregistered">尚未报名</div>

            <div class="register-btn" @click="handleRegister(exam)">
              点击报名
            </div>
          </div>

          <div
            class="exam-details"
            v-if="exam.state == 'REGISTERED'"
            @click="handleRegister(exam)"
          >
            <div>
              <div class="detail-row">
                <span class="label">姓名</span>
                <span class="value">{{ exam?.candidater?.name || "-" }}</span>
              </div>
              <!-- <div class="detail-row">
                <span class="label">手机号</span>
                <span class="value">{{ exam?.candidater?.phone || "-" }}</span>
              </div> -->
              <div class="detail-row">
                <span class="label">企业</span>
                <span class="value">{{
                  exam?.candidater?.company || "-"
                }}</span>
              </div>
            </div>
            <van-icon name="arrow" class="arrow-icon" />
          </div>

          <div
            v-if="isOptionalExamReady(exam)"
            class="optional-exam-entry"
            @click.stop="goToOptionalExam(exam)"
          >
            去考试
          </div>
        </van-cell>
      </van-list>

      <!-- 空状态 -->
      <div class="empty-state" v-if="!examList.length && !loading">
        <div class="empty-text">暂无课程信息</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import moment from "moment";
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { get, post } from "@/request.js";
import { Dialog } from "vant";

const name = ref("");
const router = useRouter();
const total = ref(0);
const loading = ref(false);
const finished = ref(false);
const examList = ref([]);
const requesting = ref(false);
const params = {
  current: 0,
  pageSize: 10,
};
const examStatus = ref({
  // 审核状态
  FILLING_IN: { text: "报名中", color: "#1890ff" },
  PENDING: { text: "待审核", color: "#faad14" },
  APPROVED: { text: "已报名", color: "#52c41a" },
  REJECTED: { text: "已拒绝", color: "#ff4d4f" },
  // 学习进度状态
  STUDING: { text: "学习", color: "#1890ff" },
  EXAM: { text: "考试", color: "#f50" },
  PASSED: { text: "完成", color: "#1890ff" },
});
// 重置并加载第一页
const resetAndFetchFirstPage = async () => {
  finished.value = false;
  await getExamList();
};

const getExamList = async () => {
  if (requesting.value || finished.value) return;
  requesting.value = true;
  try {
    const res = await post("/api/my/registration/page", params);
    if (res && res.success && res.data) {
      const list = res.data.data || [];
      examList.value = params.current == 0 ? list : examList.value.concat(list);
      const count = Number(res.data.count) || 0;
      total.value = count;
      if (examList.value.length >= total.value || list.length === 0) {
        finished.value = true;
      }
    } else {
      finished.value = true;
    }
  } catch (error) {
    finished.value = true;
    requesting.value = false;
  } finally {
    loading.value = false;
    requesting.value = false;
  }
};

// 触底加载下一页
const onLoad = async () => {
  if (finished.value) return;
  if (examList.value.length >= total.value) return;
  params.current += 1;
  await getExamList();
};
const handleRegister = (exam) => {
  router.push({
    path: "/courseDetail",
    query: {
      id: exam.id,
      courseId: exam.courseId,
    },
  });
};

const isOptionalExamReady = (exam) => {
  return (
    exam?.state === "REGISTERED" &&
    exam?.optionCourseExam === true &&
    exam?.auditStatus === "APPROVED"
  );
};

const goToOptionalExam = (exam) => {
  router.push({
    path: "/courseDetail",
    query: {
      id: exam.id,
      courseId: exam.courseId,
      step: 3,
    },
  });
};

const loginOut = () => {
  Dialog.confirm({
    title: "提示",
    message: "确定要退出登录吗？",
    confirmButtonText: "确定",
    cancelButtonText: "取消",
  })
    .then(async () => {
      const res = await post("/api/logout");
      if (res.success) {
        sessionStorage.removeItem("userInfo");
        router.replace({
          path: "/",
        });
      }
    })
    .catch(() => {
      // 用户取消，不做任何操作
    });
};

const goToUserInfo = () => {
  router.push({
    path: "/userInfo",
  });
};

const getUserInfo = async () => {
  const res = await get("/api/my/home/getInfo");
  if (res.success) {
    name.value = res.data.name || "-";
  }
};

// 格式化课程时间段：YYYY-MM-DD ~ YYYY-MM-DD（接口返回 startTime/endTime 为时间戳）
const formatCourseTimeRange = (exam) => {
  if (!exam?.startTime || !exam?.endTime) return "";
  return `${moment(exam.startTime).format("YYYY-MM-DD")} ~ ${moment(
    exam.endTime
  ).format("YYYY-MM-DD")}`;
};

// 获取状态文本
const getStatusText = (exam) => {
  // 优先使用 auditStatus
  if (exam.auditStatus && examStatus.value[exam.auditStatus]) {
    // 如果 auditStatus 是通过（APPROVED），则显示 step（学习进度状态）
    if (exam.auditStatus === "APPROVED") {
      return examStatus.value[exam.step]?.text || "";
    }
    // 否则显示 auditStatus 对应的状态
    return examStatus.value[exam.auditStatus].text;
  }
  // 如果没有 auditStatus，使用 step（学习进度状态）
  return examStatus.value[exam.step]?.text || "";
};

// 获取状态颜色
const getStatusColor = (exam) => {
  // 优先使用 auditStatus
  if (exam.auditStatus && examStatus.value[exam.auditStatus]) {
    // 如果 auditStatus 是通过（APPROVED），则显示 step（学习进度状态）的颜色
    if (exam.auditStatus === "APPROVED") {
      return examStatus.value[exam.step]?.color || "#52c41a";
    }
    // 否则显示 auditStatus 对应的颜色
    return examStatus.value[exam.auditStatus].color;
  }
  // 如果没有 auditStatus，使用 step（学习进度状态）
  return examStatus.value[exam.step]?.color || "#52c41a";
};

onMounted(() => {
  getUserInfo();
  resetAndFetchFirstPage();
});
</script>

<style scoped lang="less">
.exam-list-container {
  min-height: 100vh;
  // position: relative;
  width: 100%;
  // height: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  .header {
    position: relative;
    height: 180px;
    width: 100%;
    background-repeat: no-repeat;
    background-size: 100% 100%;
    -webkit-background-size: 100% 100%;
    -o-background-size: 100% 100%;
    background-image: url(../../../assets/imgs/mobile_back.png);
    text-align: center;
    color: #677897;
    padding-top: 46px; // 为van-nav-bar留出空间
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    .username {
      margin-bottom: 10px;
      font-size: 40px;
      font-weight: bold;
    }
    .user-info {
      font-size: 14px;
      color: #677897;
    }
  }

  .content {
    padding: 16px;
    height: 0;
    flex: 1;
    overflow-y: scroll;

    .exam-card {
      background: white;
      border-radius: 12px;
      padding: 20px;
      margin-bottom: 16px;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

      .exam-title {
        font-size: 18px;
        font-weight: 600;
        color: #333;
        padding-bottom: 12px;
        border-bottom: 1px solid #f0f0f0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        .exam-title-text {
          font-weight: 600;
          color: #333;
          flex: 1;
          padding-right: 10px;
          word-break: break-word;
          white-space: normal;
        }
        .exam-title-status {
          font-size: 16px;
        }
      }

      .course-time-range {
        font-size: 14px;
        color: #6b7280;
        margin-top: 8px;
        margin-bottom: 4px;
      }

      .registration-area {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-direction: column;

        .status-text {
          font-size: 16px;
          color: #52c41a;
          font-weight: 500;
          padding: 25px 0;

          &.unregistered {
            color: #999;
          }
        }

        .register-btn {
          background: linear-gradient(135deg, #1890ff, #36cfc9);
          color: white;
          padding: 12px 24px;
          border-radius: 25px;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.3s ease;
          font-weight: 500;
          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(24, 144, 255, 0.4);
          }

          &.disabled {
            background: #d9d9d9;
            cursor: not-allowed;

            &:hover {
              transform: none;
              box-shadow: none;
            }
          }
        }
      }

      .exam-details {
        display: flex;
        align-items: center;
        justify-content: space-between;
        .detail-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 0;
          border-bottom: 1px solid #fafafa;

          &:last-child {
            border-bottom: none;
          }

          .label {
            font-size: 16px;
            color: #333333;
            font-weight: 400;
            min-width: 60px;
          }

          .value {
            flex: 1;
            font-size: 16px;
            color: #666666;
            margin-right: 8px;
          }
        }
        .arrow-icon {
          color: #1d9af6;
          font-size: 20px;
          font-weight: bold;
        }
      }

      .optional-exam-entry {
        margin-top: 14px;
        background: #52c41a;
        color: #ffffff;
        text-align: center;
        padding: 11px 18px;
        border-radius: 24px;
        font-size: 15px;
        font-weight: 600;
        cursor: pointer;
        box-shadow: 0 4px 12px rgba(82, 196, 26, 0.24);
      }
    }

    .empty-state {
      text-align: center;
      padding: 60px 20px;

      .empty-text {
        color: #999;
        font-size: 16px;
      }
    }

    .loading-state {
      text-align: center;
      padding: 40px 20px;
    }
  }
}

// 全局样式覆盖
:deep(.van-nav-bar) {
  .van-nav-bar__title {
    font-size: 18px;
    font-weight: 600;
  }
}
</style>
